import { DatePipe } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isToday } from 'date-fns';
import { SchenckColors, UnitSettingsV2Service } from '@schenck/rotec-components';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  XAXisComponentOption,
  YAXisComponentOption,
  LegendComponentOption,
  EChartsOption,
  DataZoomComponentOption,
  SeriesOption,
  DatasetComponentOption,
  TooltipComponentOption,
} from 'echarts';
import { MainCompare } from 'src/api';
import { TimePipe } from '../time.pipe';
import { ChartColors, ChartColorService } from './chart-color.service';
import { getGaugeChartOptions } from './gauge-chart/gauge-chart';
import { getMachineEventChartOptions } from './machine-event-chart/machine-event-chart';
import {
  getMultiTrendChartOptions,
  MultitrendChartContent,
} from './multitrend-chart/multitrend-chart';
import { getPolarChartOptions, PolarChartContent } from './polar-chart/polar-chart';

export interface XAxis {
  type?: 'category' | 'time';
  name?: string;
  unit?: string;
  values: string[];
}

export interface YAxisData {
  name?: string;
  values: number[];
  unit?: string;
}

export interface SingleMeasure {
  asset: string;
  yAxisData: YAxisData;
}

export interface MultiMeasure {
  asset: string;
  yAxisData: YAxisData[];
}

/** Simple Bar Chart */
export interface SimpleBarChartContent {
  xAxis: XAxis;
  unit?: string;
  measures: SingleMeasure[];
}

/** Line Chart */
export interface LineChartContent {
  xAxis: Date[];
  unit: string;
  measures: SingleMeasure[];
}

/** BarLine Chart */
export interface BarLineChartContent {
  xAxis: XAxis;
  measures: MultiMeasure[];
}

/** StackBar Chart */
export interface StackBarMeasure {
  critical: number;
  major: number;
  minor: number;
  warning: number;
}

export interface StackBarContent {
  xAxis: Date[];
  measures: StackBarMeasure[];
}

/** Round Chart Measure */
export interface RoundChartMeasure {
  name: string;
  value: number;
}

/** Round Chart */
export interface RoundChartContent {
  asset: string;
  measures: RoundChartMeasure[];
}

/** Series Bar Data */
export interface SeriesBarData {
  value: number;
  itemStyle?: { color: string };
}

export namespace RoundChartContent {
  export function create<T>(
    data: MainCompare<T>,
    assetNames: { main: string; compare?: string },
    mapper: (t: T) => RoundChartMeasure[]
  ): RoundChartContent[] {
    const main: RoundChartContent = { asset: assetNames.main, measures: mapper(data.main) };
    if (!data.compare) {
      return [main];
    }

    const compare: RoundChartContent = {
      asset: assetNames.compare!,
      measures: mapper(data.compare),
    };
    return [main, compare];
  }
}

export type ChartType =
  | 'simplebar'
  | 'barline'
  | 'round'
  | 'multitrend'
  | 'polar'
  | 'line'
  | 'stackbar'
  | 'gauge'
  | 'event';

export interface Chart<Type = ChartType, Data = any> {
  chartType: Type;
  data: Data;
  config?: ChartConfig;
  height?: number;
}

export interface ZoomState {
  start: number;
  end: number;
}

/**
 * Additional configuration for the chart
 */
export interface ChartConfig {
  /**
   * If the data should be truncated from a certain length.
   * Used in: SimpleBar
   */
  truncate?: boolean;
  /**
   * If the YAxis should be in percent. Determines if balancing run chart or rotor ranking chart should be configured
   * Used in: SimpleBar
   */
  percentAxis?: boolean;
  /**
   * For which plane the data should be shown
   * Used in: Multitrend
   */
  selectedPlane?: number;
  /**
   * If it should be possible to zoom in the chart
   * Used in: SimpleBar, Line, BarLine, Stackbar
   */
  zoom?: boolean;
  /**
   * Zoom state after repainting the chart
   */
  zoomState?: ZoomState;
  /**
   * Which elements are selected in the legend
   */
  selectedState?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ChartService {
  private readonly timePipe = inject(TimePipe);
  private readonly colorService = inject(ChartColorService);
  private readonly translate = inject(TranslateService);
  private readonly settings = inject(UnitSettingsV2Service);
  private readonly datePipe = inject(DatePipe);

  /** Standard config for x-Axis used in all charts with xAxis */
  public xAxis: XAXisComponentOption = {
    axisLine: {
      show: false,
    },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: false,
    },
  };

  /** Standard config for y-Axis used in all charts with yAxis */
  public yAxis: YAXisComponentOption = {
    type: 'value',
    axisLine: {
      show: false,
    },
    axisTick: {
      show: false,
    },
  };

  /** Standard config for legend used in all charts with legend*/
  public legend: LegendComponentOption = {
    orient: 'horizontal',
    bottom: 0,
    icon: 'circle',
    textStyle: {
      color: SchenckColors.SCHENCK_COLOR_GRAY_LIGHT,
    },
  };

  /**
   *
   * @param data
   * the data received from the backend, should be of type RoundChartContent, for the configuration to be correct
   */
  public getPieChartOptions(
    data: RoundChartContent[],
    darkMode?: boolean,
    chartConfig?: ChartConfig
  ): EChartsOption[] {
    const config = chartConfig ? chartConfig : {};
    return data.map((content, index) => {
      const echartOption: EChartsOption = {
        color:
          index === 0
            ? ChartColorService.getColors(ChartColors.BLUE, darkMode)
            : ChartColorService.getColors(ChartColors.GREEN, darkMode),
        tooltip: {
          trigger: 'item',
          formatter: '{d}% ({b})',
          position: 'right',
          confine: true,
        },
        legend: {
          ...this.legend,
          selected: config.selectedState ? config.selectedState : {},
        },
        series: [
          {
            type: 'pie',
            radius: ['50%', '70%'],
            avoidLabelOverlap: false,
            label: {
              show: false,
              position: 'center',
            },
            emphasis: {
              label: {
                show: true,
                formatter: (params: any) => {
                  return this.timePipe.transform(params.value);
                },
              },
            },
            data: content.measures,
          },
        ],
      };
      return echartOption;
    });
  }

  /**
   *
   * @param data
   * the data received from the backend, should be of type SimpleBarChartContent, for the configuration to be correct
   *
   * @param chartConfig
   * additional config for the chart
   * if chartConfig.percentAxis is set to true, the options for the balancing run chart on the quality page are configured
   * if not the options for the rotor ranking chart on the statistics are configured
   *
   * if chartConfig.truncate is set to true, the data will be truncated to the specified number of elements
   *
   * if chartConfig.zoom is set to true, it will be possible to zoom in the chart
   */
  private getSimpleBarChartOptions(
    data: SimpleBarChartContent,
    darkMode: boolean,
    chartConfig?: ChartConfig
  ): EChartsOption[] {
    const config = chartConfig ? chartConfig : {};
    const unit = data.unit ? data.unit : '';
    let colorLength = 0;
    let seriesData: SeriesBarData[] = [];
    const values: number[][] = [[], []];
    const series: SeriesOption[] = [];
    const dataZoom: DataZoomComponentOption[] = [];

    // Remove Rotors with empty or 0 values
    const idxEmpty = [];
    const hasCompare = data.measures.length > 1;

    for (let i = 0; i < data.xAxis.values.length; i++) {
      if (
        !data.measures[0].yAxisData.values[i] &&
        (!hasCompare ||
          !data.measures[1].yAxisData.values[i] ||
          data.measures[1].yAxisData.values[i] === 0)
      ) {
        idxEmpty.push(i);
      }
    }

    const tmpXAxis = data.xAxis.values;
    const tmpYAxis0 = data.measures[0].yAxisData.values;
    const tmpYAxis1 = data.measures[1]?.yAxisData.values;

    data.xAxis.values = [];
    data.measures[0].yAxisData.values = [];

    if (hasCompare) {
      data.measures[1].yAxisData.values = [];
    }

    for (let i = 0; i < tmpXAxis.length; i++) {
      if (typeof idxEmpty.find((item) => item === i) === 'undefined') {
        data.xAxis.values.push(tmpXAxis[i]);
        data.measures[0].yAxisData.values.push(tmpYAxis0[i]);

        if (hasCompare) {
          data.measures[1].yAxisData.values.push(tmpYAxis1[i]);
        }
      }
    }

    data.measures.map((measure, measureIndex) => {
      colorLength++;

      if (config.percentAxis) {
        const total = measure.yAxisData.values.reduce((a, b) => a + b, 0);
        seriesData = measure.yAxisData.values.map((value, valueIindex) => {
          (values[measureIndex] || []).push(value);
          return {
            value: (value * 100) / total,
            itemStyle: {
              color: this.colorService.getBalancingRunColor(measureIndex, valueIindex, darkMode),
            },
          };
        });
      } else {
        seriesData = measure.yAxisData.values.map((value) => {
          return {
            value: value,
          };
        });
      }

      series.push(<SeriesOption>{
        type: 'bar',
        symbolSize: 8,
        barGap: data.measures.length > 1 ? '0' : '10',
        barMaxWidth: 30,
        name: config.percentAxis ? measure.asset : '',
        data: config.truncate
          ? this.truncateData(seriesData, 10)
          : config.percentAxis
            ? seriesData
            : this.truncateData(seriesData, 30),
      });
    });

    if (config.zoom) {
      dataZoom.push({
        type: 'inside',
        yAxisIndex: [0],
        filterMode: 'none',
        start: config.zoomState ? config.zoomState.start : 0,
        end: config.zoomState ? config.zoomState.end : 100,
      });
    }

    return [
      {
        grid: {
          top: 10,
          left: 40,
          right: 20,
          bottom: 100,
        },
        tooltip: {
          trigger: 'item',
          confine: true,
          formatter: (params: any) => {
            return config.percentAxis
              ? `${params.marker} ${
                  params.seriesName ? params.seriesName : ''
                } <br> ${params.value.toFixed(0)}${unit} (${
                  values[params.seriesIndex][params.dataIndex]
                })`
              : `${params.marker} ${params.name}:  ${params.value}${unit}` +
                  (params.seriesName ? ` (${params.seriesName})` : '');
          },
        },
        color: [
          ChartColorService.getColors(ChartColors.BLUE, darkMode)[0],
          ChartColorService.getColors(ChartColors.GRAY, darkMode)[0],
        ],
        xAxis: {
          ...this.xAxis,
          type: 'category',
          name: data.xAxis.name ? data.xAxis.name : '',
          nameLocation: 'middle',
          nameGap: 30,
          data: config.truncate
            ? this.truncateData(data.xAxis.values, 10)
            : config.percentAxis
              ? data.xAxis.values
              : this.truncateData(data.xAxis.values, 30),
          axisLabel: {
            rotate: !config.percentAxis ? 60 : 0,
            formatter: (value: string) => {
              return value.length > 10 ? `${value.substr(0, 10)}...` : value;
            },
          },
        },
        yAxis: {
          ...this.yAxis,
          ...{ minInterval: 1 },
          axisLabel: {
            align: 'center',
            // formatter: '{value}' + unit,
          },
          max: config.percentAxis ? 100 : undefined,
          min: config.percentAxis ? 0 : undefined,
          offset: 10,
          splitLine: {
            show: !!config.percentAxis,
          },
        },
        dataZoom: dataZoom,
        series: series,
      },
    ];
  }

  /**
   *
   * @param data
   * the data received from the backend, should be of type BarLineChartContent, for the configuration to be correct
   *
   * @param chartConfig
   * additional config for the chart
   * if chartConfig.zoom is set to true, it will be possible to zoom in the chart
   */
  public getBarLineChartOptions(
    data: BarLineChartContent,
    darkMode: boolean,
    chartConfig?: ChartConfig
  ): EChartsOption[] {
    const config = chartConfig ? chartConfig : {};
    const decimals = this.settings.state.units.decimals;
    const series: SeriesOption[] = [];
    const yAxis: YAXisComponentOption[] = [];
    const dataLegend: string[] = [];
    const dataZoom: DataZoomComponentOption[] = [];
    const countMachines = data.measures.length;
    const isCategoryChart = !data.xAxis.type || data.xAxis.type === 'category';

    data.measures.forEach((measure) => {
      measure.yAxisData.forEach((yAxisValue, index) => {
        const name = yAxisValue.name
          ? `${this.translate.instant(yAxisValue.name)} ${measure.asset}`
          : '';

        series.push({
          yAxisIndex: index,
          type: yAxisValue.unit ? 'line' : 'bar',
          symbol: 'circle',
          symbolSize: 8,
          barWidth:
            data.measures.length > 1 && measure.yAxisData.length > 1
              ? 10
              : measure.yAxisData.length > 1
                ? 20
                : 30,
          barGap: data.measures.length > 1 || measure.yAxisData.length > 1 ? '0' : '10',
          name: name,
          // TODO Fix the data
          // Only category data can deal with implicit x axis specification. For others, x axis point has to be set
          data: isCategoryChart
            ? JSON.parse(yAxisValue.values.toString())
            : yAxisValue.values.map((val, idx) =>
                index === 0
                  ? // only apply no of decimals to first measures which is throughput time in seconds
                    [data.xAxis.values[idx], val.toFixed(decimals).toString()]
                  : [data.xAxis.values[idx], val.toString()]
              ),
        });

        yAxis.push(<YAXisComponentOption>{
          ...this.yAxis,
          splitLine: {
            show: !!yAxisValue.unit,
          },
          axisLabel: {
            formatter: yAxisValue.unit ? `{value}${yAxisValue.unit}` : '{value}',
          },
          minInterval: index === 1 ? 1 : 0,
        });

        dataLegend.push(name);

        if (config.zoom && measure.yAxisData.length > 1) {
          dataZoom.push({
            type: 'inside',
            xAxisIndex: [0],
            filterMode: 'empty',
            start: config.zoomState ? config.zoomState.start : 0,
            end: config.zoomState ? config.zoomState.end : 100,
          });
        }
      });
    });

    return [
      {
        grid: {
          top: 10,
          left: 50,
          bottom: 60,
        },
        textStyle: { color: SchenckColors.SCHENCK_COLOR_GRAY_LIGHT },
        legend: {
          ...this.legend,
          padding: [15, 0, 0, 0],
          data: dataLegend,
          type: 'scroll',
          selected: config.selectedState ? config.selectedState : undefined,
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            let _tooltip = '';
            for (let i = 0; i < params.length; i++) {
              const param = params[i];
              _tooltip += `${param.marker} ${param.data[1]}${
                param.seriesType === 'line' ? 's' : ''
              } <br>`;
            }
            return _tooltip;
          },
        },
        color: this.colorService.getBarLineColors(countMachines, darkMode),
        xAxis: <XAXisComponentOption>{
          type: data.xAxis.type || 'category',
          ...this.xAxis,
          // Leave out data for types other than category because they are already specified inside data points
          data: isCategoryChart ? data.xAxis.values : undefined,
          axisLabel: {
            hideOverlap: true,
          },
        },
        dataZoom,
        yAxis,
        series,
      },
    ];
  }

  /**
   *
   * @param data
   * the data received from the backend, should be of type LineChartContent, for the configuration to be correct
   *
   * @param chartConfig
   * additional config for the chart
   * if chartConfig.zoom is set to true, it will be possible to zoom in the chart
   */
  public getLineChartOptions(
    data: LineChartContent,
    darkMode: boolean,
    chartConfig?: ChartConfig
  ): EChartsOption[] {
    const config = chartConfig ? chartConfig : {};
    const unit = data.unit;
    const decimals = this.settings.state.units.decimals;
    const series: SeriesOption[] = [];
    const xAxis: any[] = data.xAxis;
    const datasetSource = [xAxis];

    data.measures.forEach((measure, index) => {
      const yAxis: any[] = measure.yAxisData.values.map((val) => val.toFixed(decimals));
      datasetSource.push(yAxis);
      series.push({
        name: 'NIT-' + index,
        type: 'line',
        lineStyle: {
          width: 4,
        },
        symbolSize: 8,
        symbol: 'circle',
        itemStyle: {
          borderColor: SchenckColors.SCHENCK_COLOR_WHITE,
          borderWidth: 2,
        },
        seriesLayoutBy: 'row',
        encode: {
          x: 0,
          y: index + 1,
        },
      });
    });
    const dataset: DatasetComponentOption = {
      source: datasetSource,
    };

    return [
      {
        grid: {
          top: 10,
          left: 40,
          right: 20,
        },
        tooltip: {
          trigger: 'item',
          confine: true,
          formatter: (params: any) => {
            return (
              params.marker +
              // x-axis value: transform to local date
              this.datePipe.transform(params.data[0]) +
              ': ' +
              params.data[params.encode.y[0]] +
              unit
            );
          },
        },
        color: [
          ChartColorService.getColors(ChartColors.BLUE, darkMode)[0],
          ChartColorService.getColors(ChartColors.GRAY, darkMode)[0],
        ], // because there can max be two lines in this chart
        xAxis: {
          type: 'time',
          ...this.xAxis,
          axisLabel: {
            hideOverlap: true,
          },
        },
        yAxis: <YAXisComponentOption>{
          ...this.yAxis,
          offset: 15,
          axisLabel: {
            align: 'center',
            formatter: '{value}' + unit,
          },
        },
        dataZoom: config.zoom
          ? [
              {
                type: 'inside',
                xAxisIndex: [0],
                filterMode: 'none',
                start: config.zoomState ? config.zoomState.start : 0,
                end: config.zoomState ? config.zoomState.end : 100,
              },
            ]
          : [],
        dataset: dataset,
        series: series,
        textStyle: { color: SchenckColors.SCHENCK_COLOR_GRAY_LIGHT },
      },
    ];
  }

  /**
   *
   * @param data
   * the data received from the backend, should be of type StackBarContent, for the configuration to be correct
   *
   * @param chartConfig
   * additional config for the chart
   * if chartConfig.zoom is set to true, it will be possible to zoom in the chart
   */
  public getStackBarOptions(
    data: StackBarContent,
    darkMode: boolean,
    chartConfig?: ChartConfig
  ): EChartsOption[] {
    const config = chartConfig ? chartConfig : {};
    const xAxisData: any = ['xAxisData', ...data.xAxis];
    const critical: any[] = ['Critical'];
    const major: any[] = ['Major'];
    const minor: any[] = ['Minor'];
    const warning: any[] = ['Warning'];

    data.measures.map((day) => {
      critical.push(day.critical);
      major.push(day.major);
      minor.push(day.minor);
      warning.push(day.warning);
    });

    const datasetSource = [xAxisData, critical, major, minor, warning];

    const series: SeriesOption[] = datasetSource
      .filter(
        (source) =>
          !source.some((el: any) => (typeof el === 'string' ? el.includes('xAxisData') : false))
      )
      .map((source, index) => {
        return {
          name: this.translate.instant(`MC_Severity_${source[0]}`),
          type: 'bar',
          itemStyle: {
            color: ChartColorService.eventToColorMapping[source[0]](darkMode),
          },
          stack: 'Day',
          seriesLayoutBy: 'row',
          encode: {
            x: 0,
            y: index + 1,
          },
        };
      });

    const tooltip: TooltipComponentOption = {
      formatter: (params: any) => {
        return `${params.marker} ${params.seriesName}: ${params.data[params.seriesIndex + 1]}`;
      },
      trigger: 'item',
    };

    return [
      {
        grid: {
          top: 20,
          bottom: 60,
        },
        tooltip: tooltip,
        legend: {
          ...this.legend,
          selected: config.selectedState ? config.selectedState : undefined,
        },
        xAxis: <XAXisComponentOption>{
          ...this.xAxis,
          type: 'category',
          axisLabel: {
            formatter: (value: string | number | Date) => {
              return isToday(value as number | Date)
                ? this.translate.instant('MC_Today')
                : this.datePipe.transform(value);
            },
          },
        },
        yAxis: <YAXisComponentOption>{
          ...this.yAxis,
          offset: 20,
          minInterval: 1,
        },
        dataset: {
          source: datasetSource,
        },
        dataZoom: config.zoom
          ? [
              {
                type: 'inside',
                xAxisIndex: [0],
                filterMode: 'none',
                start: config.zoomState ? config.zoomState.start : 0,
                end: config.zoomState ? config.zoomState.end : 100,
              },
            ]
          : [],
        series: series,
      },
    ];
  }

  public getMultitrendChartOptions(
    data: MultitrendChartContent,
    darkMode: boolean,
    chartConfig?: ChartConfig
  ): EChartsOption[] {
    return getMultiTrendChartOptions({
      translate: this.translate,
      colorService: this.colorService,
      settingsService: this.settings,
      data,
      chartConfig,
      darkMode,
    });
  }

  public getPolarChartOptions(
    data: PolarChartContent,
    darkMode: boolean,
    chartConfig?: ChartConfig
  ): EChartsOption[] {
    return getPolarChartOptions({
      content: data,
      translate: this.translate,
      settings: this.settings,
      darkMode,
      chartConfig,
    });
  }

  public getGaugeChartOptions(data: any): EChartsOption[] {
    return getGaugeChartOptions(data, this.translate);
  }

  public getMachineEventChartOptions(
    data: any,
    darkMode?: boolean,
    chartConfig?: ChartConfig
  ): EChartsOption[] {
    return getMachineEventChartOptions(data, this.translate, darkMode, chartConfig);
  }

  public getChartOptions(
    chart$: BehaviorSubject<Chart | undefined>,
    darkMode: boolean
  ): Observable<EChartsOption[]> {
    return chart$.pipe(
      map((chart: Chart) => {
        let options: EChartsOption[] = [];

        if (chart) {
          switch (chart.chartType) {
            case 'simplebar':
              options = this.getSimpleBarChartOptions(chart.data, darkMode, chart.config);
              break;
            case 'barline':
              options = this.getBarLineChartOptions(chart.data, darkMode, chart.config);
              break;
            case 'round':
              options = this.getPieChartOptions(chart.data, darkMode, chart.config);
              break;
            case 'multitrend':
              options = this.getMultitrendChartOptions(chart.data, darkMode, chart.config);
              break;
            case 'polar':
              options = this.getPolarChartOptions(chart.data, darkMode, chart.config);
              break;
            case 'line':
              options = this.getLineChartOptions(chart.data, darkMode, chart.config);
              break;
            case 'stackbar':
              options = this.getStackBarOptions(chart.data, darkMode, chart.config);
              break;
            case 'gauge':
              options = this.getGaugeChartOptions(chart.data);
              break;
            case 'event':
              options = this.getMachineEventChartOptions(chart.data, darkMode, chart.config);
              break;
          }
        } else {
          options = [];
        }
        return options;
      })
    );
  }

  /**
   * Helper function to truncate data to specified length
   *
   * @param data
   * data for xAxis
   */
  private truncateData(data: any[], maxLength: number): any[] {
    return data.length >= maxLength ? data.slice(0, maxLength) : data;
  }
}
